import { setUserIconColor, showModal, StatBubble } from 'components/AppShell';
import { Icon } from 'components/Icon';
import { signOut } from 'components/Login';
import { type JSXChild, render } from 'engine/index';
import { Check, Hourglass, Trophy, User } from 'lucide-static';
import {
	type BouwHetLandschapAnswer,
	type BouwHetLandschapPitchAnswer,
	type BouwHetLandschapPitchNew,
	type BouwHetLandschapPreparePitch,
	type BouwHetLandschapQuestion,
	type BouwHetLandschapUpdate,
} from 'shared/modelTypes/bouw-het-landschap';
import { lateinit, unreachable } from 'shared/util';
import { Game } from './game';
import { rpc, State } from 'engine/rpc';
import { Timer } from 'components/Timer';
import { BottomRight } from 'components/BottomRight';
import { marked } from 'marked';

export class BouwHetLandschap extends Game<'bouw-het-landschap-spel'> {
	override maxPlayers: number = 4;

	scoreEl: HTMLElement = lateinit;

	answers: Record<string, BouwHetLandschapAnswer> = {};
	pitches: BouwHetLandschapPitchAnswer[] = [];

	override getGameName(): string {
		return 'Bouw het landschap spel';
	}

	override defineBottomUI(): JSXChild {
		return (
			<>
				<StatBubble color="green" label="Punten" value="0" id="score" />
			</>
		);
	}
	override onShellCreated(view: HTMLElement): void {
		super.onShellCreated(view);
		this.scoreEl = view.querySelector('#score')!;
	}

	override handleUpdate(update: BouwHetLandschapUpdate): void {
		const sessionId = State.gameId.toString();
		const playerId = State.playerId;
		const kind = update.kind;
		// const playerId = State.playerId;

		// console.log('message', update);

		switch (kind) {
			case 'trigger-effect':
				this.triggerEffect(update);
				break;
			case 'show-login-screen':
				this.showLoginScreen();
				break;
			case 'show-register-screen':
				this.showRegisterScreen('team');
				break;
			case 'update-players-state':
				this.updatePlayersState(update);
				break;
			case 'update-player-state':
				State.name = update.player.name;
				State.color = update.player.color;
				State.score = update.player.score;
				this.answers = {};
				this.pitches = [];

				this.shellNameEl.innerText = update.player.name;
				setUserIconColor(this.userIconEl, update.player.color);
				this.scoreEl.innerText = (State.score ?? 0).toString();
				break;
			case 'show-questions-screen':
				{
					render(
						this.shellContentEl,
						<QuestionsScreen
							round={update.round}
							name={update.name}
							questions={update.questions}
							answers={update.answers}
							onAnswer={(answers) => {
								rpc.sendBouwHetLandschapMessage.query({
									sessionId,
									update: {
										kind: 'submit-answer',
										playerId: playerId,
										answers: answers,
									},
								});
							}}
							timerFrom={update.timerFrom}
							timerTo={update.timerTo}
						/>,
					);
				}
				break;
			case 'show-wait-screen':
				{
					render(this.shellContentEl, <WaitScreen title={update.title} message={update.message} />);
				}
				break;
			case 'show-choose-topic-screen':
				{
					render(
						this.shellContentEl,
						<ChooseTopic
							topics={update.topics}
							unavailableTopics={update.unavailableTopics}
							onChoose={(topic) => {
								rpc.sendBouwHetLandschapMessage.query({
									sessionId,
									update: {
										kind: 'select-topic',
										playerId,
										topic,
									},
								}),
									topic.key !== 'JK'
										? render(
												this.shellContentEl,
												<TopicPresentingScreen
													topic={topic}
													onSubmit={() => {
														render(
															this.shellContentEl,
															<LayTileScreen
																onSubmit={() => {
																	rpc.sendBouwHetLandschapMessage.query({
																		sessionId,
																		update: {
																			kind: 'submit-topic',
																			playerId,
																		},
																	});
																}}
															/>,
														);
													}}
												/>,
											)
										: render(
												this.shellContentEl,
												<LayTileScreen
													onSubmit={() => {
														rpc.sendBouwHetLandschapMessage.query({
															sessionId,
															update: {
																kind: 'submit-topic',
																playerId,
															},
														});
													}}
												/>,
											);
							}}
						/>,
					);
				}
				break;
			case 'show-topic-screen':
				break;
			case 'show-results-screen':
				render(this.shellContentEl, <ResultsScreen results={update.players} />);
				break;
			case 'theme-and-area-presenting-screen':
				render(this.shellContentEl, <ThemeAndAreaPresentingScreen questions={update.questions} answers={update.answers} />);
				break;
			case 'prepare-pitch-screen':
				render(
					this.shellContentEl,
					<PreparePitchScreen
						topics={update.topics}
						pitches={this.pitches}
						onAnswer={(pitches) => {
							rpc.sendBouwHetLandschapMessage.query({
								sessionId,
								update: {
									kind: 'submit-pitch',
									playerId: playerId,
									pitches: pitches,
								},
							});
						}}
						timerFrom={update.timerFrom}
						timerTo={update.timerTo}
					/>,
				);
				break;
			case 'show-pitch-screen':
				render(this.shellContentEl, <ShowPitchScreen pitch={update.pitch} timerFrom={update.timerFrom} timerTo={update.timerTo} />);
				break;
			case 'show-review-pitch-screen':
				render(
					this.shellContentEl,
					<ShowReviewPitchScreen
						participants={update.participants}
						onChoose={(playerId) => {
							rpc.sendBouwHetLandschapMessage.query({
								sessionId,
								update: {
									kind: 'review-pitch',
									playerId: playerId,
								},
							});
							render(
								this.shellContentEl,
								<WaitScreen
									title="Wacht tot iedereen gestemd heeft"
									message="Zodra iedereen gestemd heeft begint de volgende pitch."
								/>,
							);
						}}
						timerFrom={update.timerFrom}
						timerTo={update.timerTo}
					/>,
				);
				break;
			case 'show-choose-diceroll-screen':
				break;
			default:
				unreachable(kind);
		}
	}
}

function ThemeAndAreaPresent({
	id,
	questionNumber,
	question,
	answer,
}: {
	id: string;
	questionNumber: number;
	question: BouwHetLandschapQuestion;
	answer: BouwHetLandschapAnswer;
}) {
	const questionButton: HTMLElement = (
		<button
			on:click={() =>
				showModal((confirm) => {
					const formModal: HTMLElement = (
						<div class="max-h-[90vh] overflow-y-auto rounded-lg bg-white p-6">
							<h4 class="mb-2 text-lg font-bold text-gray-900">{`Vraag ${questionNumber}`}</h4>
							<p __innerHTML={marked(question.question)}></p>
							<form
								on:submit={(e: SubmitEvent) => {
									questionButton.removeAttribute('data-done');
									confirm();
								}}
							>
								<div class="py-6">
									<label class="font-semibold text-gray-800">Omgeving:</label>
									<input
										name="answer"
										rows="5"
										placeholder="Antwoord"
										class="block w-full rounded-md border border-gray-200 bg-gray-50 p-2"
										disabled="true"
										value={answer.answer}
									/>

									<label class="font-semibold text-gray-800">Details:</label>
									<textarea
										name="details"
										rows="5"
										placeholder="Beschrijving"
										disabled="true"
										class="block w-full rounded-md border border-gray-200 bg-gray-50 p-2"
									>
										{answer.details}
									</textarea>
								</div>
								<div class="flex justify-between">
									<button type="submit" class="rounded-md bg-theme-600 p-3 text-theme-50">
										Sluiten
									</button>
								</div>
							</form>
						</div>
					);
					return formModal;
				})
			}
			data-done={undefined}
			class={'group relative block rounded-md bg-theme-600 p-3 text-center text-white'}
		>
			{id}

			<div class="absolute -right-1 -top-1 z-[1] grid size-6 place-items-center rounded-full border-b border-gray-300 bg-white opacity-0 shadow-sm group-data-[done]:opacity-100">
				<Icon icon={Check} class="size-5 text-green-600" />
			</div>
		</button>
	);

	return questionButton;
}

function Question({
	name,
	questionNumber,
	question,
	answers,
	onAnswer,
}: {
	name: string;
	questionNumber: number;
	question: { id: string; question: string };
	answers: Record<string, BouwHetLandschapAnswer>;
	onAnswer: (answer: Record<string, BouwHetLandschapAnswer>) => void;
}) {
	const questionButton: HTMLElement = (
		<button
			on:click={() =>
				showModal((confirm, cancel) => {
					const answer: BouwHetLandschapAnswer = answers[question.id] || { answer: '', details: '' };

					const formModal: HTMLElement = (
						<div class="max-h-[90vh] overflow-y-auto rounded-lg bg-white p-6">
							<h4 class="mb-2 text-lg font-bold text-gray-900">{`Vraag ${questionNumber}`}</h4>
							{/* <p __innerHTML={marked(question.area ? text.replaceAll('[AREA]', `<b>${question.area}</b>`) : text)}></p> */}
							<p __innerHTML={marked(question.question)}></p>
							<form
								on:submit={(e: SubmitEvent) => {
									e.preventDefault();
									const form = e.target as HTMLFormElement;
									const data = new FormData(form);
									const answerText = ((data.get('answer') ?? '') as string).trim();
									const detailsText = ((data.get('details') ?? '') as string).trim();
									answer.answer = answerText;
									answer.details = detailsText;
									answers[question.id] = answer;
									onAnswer(answers);
									if (answerText && answerText.length && detailsText && detailsText.length) {
										questionButton.setAttribute('data-done', '');
									} else {
										questionButton.removeAttribute('data-done');
									}
									confirm();
								}}
							>
								<div class="py-6">
									<label class="font-semibold text-gray-800">{name}:</label>
									<input
										name="answer"
										rows="5"
										placeholder="Antwoord"
										class="block w-full rounded-md border border-gray-200 bg-gray-50 p-2"
										value={answer.answer}
									/>

									<label class="font-semibold text-gray-800">Details:</label>
									<textarea
										name="details"
										rows="5"
										placeholder="Beschrijving"
										class="block w-full rounded-md border border-gray-200 bg-gray-50 p-2"
									>
										{answer.details}
									</textarea>
								</div>
								<div class="flex justify-between">
									<button on:click={cancel} type="button" class="p-3 text-gray-600">
										Annuleren
									</button>
									<button type="submit" class="rounded-md bg-theme-600 p-3 text-theme-50">
										Opslaan
									</button>
								</div>
							</form>
						</div>
					);
					return formModal;
				})
			}
			data-done={answers[question.id] && answers[question.id].answer != '' && answers[question.id].details != ''}
			class={'group relative block rounded-md bg-theme-600 p-3 text-center text-white'}
		>
			{`Vraag ${questionNumber}`}

			<div class="absolute -right-1 -top-1 z-[1] grid size-6 place-items-center rounded-full border-b border-gray-300 bg-white opacity-0 shadow-sm group-data-[done]:opacity-100">
				<Icon icon={Check} class="size-5 text-green-600" />
			</div>
		</button>
	);

	return questionButton;
}

export function ThemeAndAreaPresentingScreen({
	questions,
	answers,
}: {
	questions: BouwHetLandschapQuestion[];
	answers: Record<string, BouwHetLandschapAnswer>;
	// questions: { id: string; question: string; theme: string; area?: string | undefined; details?: string }[];
}) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-balance text-center text-2xl font-bold leading-tight text-gray-900">Deel jullie ideeën met de groep.</h2>
			<div class="flex w-full flex-col gap-5 sm:flex-row">
				<div class="flex-1 shrink-0 rounded-lg border border-theme-200 bg-theme-50 p-4 text-theme-950 shadow-md shadow-theme-200/50">
					<div class="flex flex-col gap-3">
						{questions.map((question, index) => {
							const answer: BouwHetLandschapAnswer = answers[question.id] || { answer: '', details: '' };
							return <ThemeAndAreaPresent id={question.id} questionNumber={index + 1} question={question} answer={answer} />;
						})}
					</div>
				</div>
			</div>
		</div>
	);
}

export function TopicPresentingScreen({ topic, onSubmit }: { topic: Topic; onSubmit: () => void }) {
	// const text = topic.question.replaceAll('[THEME]', `"${topic.theme}"`);
	const answer: BouwHetLandschapAnswer = topic.answer || { answer: '', details: '', playerId: '' };

	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-balance text-center text-2xl font-bold leading-tight text-gray-900">Deel jullie input met de groep.</h2>
			<div class="flex w-full flex-col gap-5 sm:flex-row">
				<div class="flex-1 shrink-0">
					<div class="flex flex-col gap-3">
						<div class="rounded-lg bg-white p-6">
							{/* <p>{topic.area ? text.replaceAll('[AREA]', topic.area) : text}</p> */}
							<p __innerHTML={marked(topic.question)}></p>

							<form
								on:submit={(e: SubmitEvent) => {
									onSubmit();
								}}
							>
								<div class="py-6">
									<label class="font-semibold text-gray-800">Omgeving:</label>
									<input
										name="answer"
										rows="5"
										placeholder="Antwoord"
										class="block w-full rounded-md border border-gray-200 bg-gray-50 p-2"
										disabled="true"
										value={answer.answer}
									/>

									<label class="font-semibold text-gray-800">Details:</label>
									<textarea
										name="details"
										rows="5"
										placeholder="Beschrijving"
										disabled="true"
										class="block w-full rounded-md border border-gray-200 bg-gray-50 p-2"
									>
										{answer.details}
									</textarea>
								</div>
								<div class="flex justify-between">
									<button type="submit" class="rounded-md bg-theme-600 p-3 text-theme-50">
										Doorgaan
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export function PreparePitchScreen({
	topics,
	pitches,
	onAnswer,
	timerFrom,
	timerTo,
}: {
	topics: BouwHetLandschapPreparePitch[];
	pitches: BouwHetLandschapPitchAnswer[];
	onAnswer: (pitches: BouwHetLandschapPitchAnswer[]) => void;
	timerFrom: number;
	timerTo: number;
}) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-balance text-center text-2xl font-bold leading-tight text-gray-900">Bereid jullie pitch voor.</h2>
			<p class="mb-6 text-gray-700">Jullie krijgen 10 minuten de tijd om jullie pitch voor te bereiden.</p>
			<div class="flex w-full flex-col gap-5 sm:flex-row">
				<div class="flex-1 shrink-0 rounded-lg border border-theme-200 bg-theme-50 p-4 text-theme-950 shadow-md shadow-theme-200/50">
					<div class="flex flex-col gap-3">
						{topics.map((topic, index) => {
							return <PreparePitch pitchNumber={index + 1} topic={topic} pitches={pitches} onAnswer={onAnswer} />;
						})}
					</div>
				</div>
			</div>

			<BottomRight>
				<Timer timerFrom={timerFrom} timerTo={timerTo} />
			</BottomRight>
		</div>
	);
}

function PreparePitch({
	pitchNumber,
	topic,
	pitches,
	onAnswer,
}: {
	pitchNumber: number;
	topic: BouwHetLandschapPreparePitch;
	pitches: BouwHetLandschapPitchAnswer[];
	onAnswer: (pitches: BouwHetLandschapPitchAnswer[]) => void;
}) {
	const questionButton: HTMLElement = (
		<button
			on:click={() =>
				showModal((confirm, cancel) => {
					let answer = pitches.find((a) => a.id === topic.id);
					if (!answer) {
						answer = {
							id: topic.id,
							answer: '',
						};
						pitches.map((a) => (a.id === topic.id ? answer : a));
					}
					const formModal: HTMLElement = (
						<div class="max-h-[90vh] overflow-y-auto rounded-lg bg-white p-6">
							<h4 class="mb-2 text-lg font-bold text-gray-900">{`Pitch ${pitchNumber}: ${topic.theme}`}</h4>
							<p>
								<span class="font-semibold text-gray-800">Omgeving: </span>
								<i>{topic.area ? topic.area.answer : 'Geen antwoord ingevoerd'}</i>
							</p>
							<p>
								<span class="font-semibold text-gray-800">Personages: </span>
								<i>{topic.characters ? topic.characters.answer : 'Geen antwoord ingevoerd'}</i>
							</p>
							<p>
								<span class="font-semibold text-gray-800">Conflict: </span>
								<i>{topic.conflict ? topic.conflict.answer : 'Geen antwoord ingevoerd'}</i>
							</p>
							<p>
								<span class="font-semibold text-gray-800">Oplossing: </span>
								<i>{topic.solution ? topic.solution.answer : 'Geen antwoord ingevoerd'}</i>
							</p>
							<form
								on:submit={(e: SubmitEvent) => {
									e.preventDefault();
									const form = e.target as HTMLFormElement;
									const data = new FormData(form);
									const answerText = ((data.get('pitch') ?? '') as string).trim();
									answer.answer = answerText;
									const result = {
										id: topic.id,
										answer: answerText,
									};
									pitches.push(result);
									onAnswer(pitches);
									if (answerText && answerText.length) {
										questionButton.setAttribute('data-done', '');
									} else {
										questionButton.removeAttribute('data-done');
									}
									confirm();
								}}
							>
								<div class="py-6">
									<label class="font-semibold text-gray-800">Schrijf jullie pitch:</label>
									<textarea
										name="pitch"
										rows="5"
										placeholder="Pitch aantekeningen"
										class="block w-full rounded-md border border-gray-200 bg-gray-50 p-2"
									>
										{answer?.answer}
									</textarea>
								</div>
								<div class="flex justify-between">
									<button on:click={cancel} type="button" class="p-3 text-gray-600">
										Annuleren
									</button>
									<button type="submit" class="rounded-md bg-theme-600 p-3 text-theme-50">
										Opslaan
									</button>
								</div>
							</form>
						</div>
					);
					return formModal;
				})
			}
			data-done={pitches.find((p) => p.id == topic.id)?.answer ? true : undefined}
			class={'group relative block rounded-md bg-theme-600 p-3 text-center text-white'}
		>
			{`Pitch ${pitchNumber}: ${topic.theme}`}

			<div class="absolute -right-1 -top-1 z-[1] grid size-6 place-items-center rounded-full border-b border-gray-300 bg-white opacity-0 shadow-sm group-data-[done]:opacity-100">
				<Icon icon={Check} class="size-5 text-green-600" />
			</div>
		</button>
	);

	return questionButton;
}

export function QuestionsScreen({
	round,
	name,
	questions,
	answers,
	onAnswer,
	timerFrom,
	timerTo,
}: {
	round: number;
	name: string;
	questions: BouwHetLandschapQuestion[];
	answers: Record<string, BouwHetLandschapAnswer>;
	onAnswer: (answers: Record<string, BouwHetLandschapAnswer>) => void;
	timerFrom: number;
	timerTo: number;
}) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-balance text-center text-2xl font-bold leading-tight text-gray-900">
				De vragen! Jullie krijgen per ronde 10 minuten de tijd om de antwoorden op te schrijven.
			</h2>
			<div class="flex w-full flex-col gap-5 sm:flex-row">
				<div class="flex-1 shrink-0 rounded-lg border border-theme-200 bg-theme-50 p-4 text-theme-950 shadow-md shadow-theme-200/50">
					<h3 class="mb-4 block text-center text-xl font-bold leading-none">{`Ronde ${round}: ${name}`}</h3>
					<div class="flex flex-col gap-3">
						{questions.map((question, index) => {
							return (
								<Question
									name={name}
									questionNumber={2 * round - 1 + index}
									question={question}
									answers={answers}
									onAnswer={onAnswer}
								/>
							);
						})}
					</div>
				</div>
			</div>

			<BottomRight>
				<Timer timerFrom={timerFrom} timerTo={timerTo} />
			</BottomRight>
		</div>
	);
}

export function WaitScreen({ title, message }: { title: string; message: string }) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:mb-6 lg:text-6xl">{title}</h2>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">{message}</pre>
			<Icon icon={Hourglass} class="size-20 text-gray-300" />
		</div>
	);
}

type Topic = {
	key: string;
	name: string;
	question: string;
	answer?: BouwHetLandschapAnswer;
};

export function ChooseTopic({
	topics,
	unavailableTopics,
	onChoose,
}: {
	topics: Topic[];
	unavailableTopics: string[];
	onChoose: (topic: Topic) => void;
}) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:text-6xl">Jij bent aan zet!</h2>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">
				Kies een aanvulling van het verhaal of ga voor de joker:
			</pre>
			<div class="mt-8 grid w-full grid-cols-2 gap-5">
				{topics.slice(2).map((t) => {
					const disabled = unavailableTopics.includes(t.key);
					return (
						<div
							on:click={() => !disabled && onChoose(t)}
							class={`w-full cursor-pointer rounded-md border text-center shadow-md ${disabled ? 'bg-neutral-200' : 'bg-white'}`}
						>
							<span
								class={`flex justify-center rounded-md p-2 text-xl text-white ${disabled ? 'bg-neutral-700' : 'bg-theme-600'}`}
							>
								{t.key !== 'JK' ? `${t.key}: ${t.name}` : t.name}
							</span>
							{t.answer && <div class={`text-md w-full p-2 text-black`}>{t.answer.answer}</div>}
						</div>
					);
				})}
			</div>
		</div>
	);
}

export function LayTileScreen({ onSubmit }: { onSubmit: () => void }) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:mb-6 lg:text-6xl">Leg een tegel neer</h2>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">Leg een tegel neer en druk op doorgaan</pre>
			<button on:click={() => onSubmit()} class="rounded-md bg-theme-600 p-3 text-theme-50">
				Doorgaan
			</button>
		</div>
	);
}

export function ShowPitchScreen({ pitch, timerFrom, timerTo }: { pitch: BouwHetLandschapPitchNew; timerFrom: number; timerTo: number }) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-balance text-center text-2xl font-bold leading-tight text-gray-900">Presenteer jullie pitch.</h2>
			<div class="flex w-full flex-col gap-5 sm:flex-row">
				<div class="flex-1 shrink-0">
					<div class="flex flex-col gap-3">
						<div class="rounded-lg bg-white p-6">
							<h4 class="mb-2 text-lg font-bold text-gray-900">{`Pitch: ${pitch.theme}`}</h4>
							<p>
								<span class="font-semibold text-gray-800">Omgeving: </span>
								<i>{pitch.area ? pitch.area.answer : 'Geen antwoord ingevoerd'}</i>
							</p>
							<p>
								<span class="font-semibold text-gray-800">Personages: </span>
								<i>{pitch.characters ? pitch.characters.answer : 'Geen antwoord ingevoerd'}</i>
							</p>
							<p>
								<span class="font-semibold text-gray-800">Conflict: </span>
								<i>{pitch.conflict ? pitch.conflict.answer : 'Geen antwoord ingevoerd'}</i>
							</p>
							<p>
								<span class="font-semibold text-gray-800">Oplossing: </span>
								<i>{pitch.solution ? pitch.solution.answer : 'Geen antwoord ingevoerd'}</i>
							</p>
							<p>
								<span class="font-semibold text-gray-800">Pitch: </span>
								<br />
								<i>{pitch.pitch || 'Geen pitch voorbereid'}</i>
							</p>
						</div>
					</div>
				</div>
			</div>
			<BottomRight>
				<Timer timerFrom={timerFrom} timerTo={timerTo} />
			</BottomRight>
		</div>
	);
}

export function ShowReviewPitchScreen({
	participants,
	onChoose,
	timerFrom,
	timerTo,
}: {
	participants: { id: string; name: string; color: string }[];
	onChoose: (playerId: string) => void;
	timerFrom: number;
	timerTo: number;
}) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:text-6xl">Het is tijd om te stemmen!</h2>
			<pre class="mt-2 w-full whitespace-normal text-center font-sans text-gray-800">Welk team gaf de beste pitch?</pre>
			<div class="mt-8 grid w-full grid-cols-2 gap-5">
				{participants.map((p, index) => {
					return (
						<div
							on:click={() => p.id && onChoose(p.id)}
							class="w-full cursor-pointer rounded-md border bg-theme-600 text-center shadow-md"
						>
							<span class="flex justify-center p-2 text-xl text-white">Pitch: {index + 1}</span>
							<div class="text-md flex w-full items-center justify-center gap-2 bg-white p-2 text-black">
								<div user-icon class={`bg-${p.color}-500 rounded-full p-1 text-theme-200`}>
									<Icon icon={User} class="size-4 shrink-0 text-white" />
								</div>
								{p.name}
							</div>
						</div>
					);
				})}
			</div>
			<BottomRight>
				<Timer timerFrom={timerFrom} timerTo={timerTo} />
			</BottomRight>
		</div>
	);
}

export function ResultsScreen({ results }: { results: { name: string; color: string; score: number }[] }) {
	return (
		<div class="flex w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:mb-6 lg:text-6xl">Einduitslag</h2>
			<table class="overflow-hidden rounded-md shadow-sm">
				<thead>
					<tr class="bg-gray-100">
						<th class="px-5 py-1">Rank</th>
						<th class="px-5 py-1">Pion</th>
						<th class="px-5 py-1">Naam</th>
						<th class="px-5 py-1">Punten</th>
					</tr>
				</thead>
				<tbody>
					{results.map((r, i) => {
						const highlight = State.color == r.color;

						return (
							<tr class={highlight ? 'bg-yellow-100 ring-2 ring-yellow-400' : 'even:bg-gray-50'}>
								<td class="px-5 py-1">
									<div class="inline-flex items-baseline gap-3">
										<span class="font-bold">{i + 1}</span>
										{i == 0 && <Icon icon={Trophy} class="size-4 text-orange-400" />}
										{i == 1 && <Icon icon={Trophy} class="size-4 text-gray-400" />}
										{i == 2 && <Icon icon={Trophy} class="size-4 text-orange-700" />}
										{i >= 3 && <Icon icon={Trophy} class="size-4 opacity-0" />}
									</div>
								</td>
								<td class="px-5 py-1">{r.color}</td>
								<td class="px-5 py-1">{r.name}</td>
								<td class="px-5 py-1">{r.score}</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			<button class="rounded-md bg-gray-200 p-4 text-gray-900" on:click={signOut}>
				Spel afsluiten
			</button>
		</div>
	);
}
